<template>
  <section class="report-chapter regulate-plan-chapter">
    <ReportChapterTitle :title="chapterTitle || '三 调理方案'" />

    <template v-if="hasAcupointSection">
      <ReportRibbon title="（一）穴位保健" />
      <div class="blue-note" v-if="acupointIntro">
        {{ acupointIntro }}
      </div>
      <div
        class="paragraphs acupoint-intro-details"
        v-if="acupoint.method || acupoint.getAcupoint"
      >
        <p v-if="acupoint.method">
          <strong>【方法】</strong>{{ stripFieldLabel(acupoint.method, '方法') }}
        </p>
        <p v-if="acupoint.getAcupoint">
          <strong>【取穴】</strong>{{ stripFieldLabel(acupoint.getAcupoint, '取穴') }}
        </p>
      </div>
      <div v-if="acupointList.length" class="item-list acupoint-list">
        <div
          v-for="(item, index) in acupointList"
          :key="item.name + '-' + index"
          class="plan-item"
          :class="{ 'plan-item--no-thumb': !item.img }"
        >
          <div class="item-thumb" v-if="item.img">
            <img :src="item.img" :alt="item.name" />
          </div>
          <div class="item-body">
            <div class="item-title" v-if="item.name">{{ item.name }}</div>
            <div v-if="item.position" class="line">
              <strong>【位置】</strong>{{ stripFieldLabel(item.position, '位置') }}
            </div>
            <div v-if="item.getPointMethod" class="line">
              <strong>【取穴】</strong>{{ stripFieldLabel(item.getPointMethod, '取穴') }}
            </div>
            <div v-if="item.meridian" class="line">
              <strong>【归经】</strong>{{ stripFieldLabel(item.meridian, '归经') }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-if="medicineList.length">
      <ReportRibbon title="（二）药食康养" />
      <div class="item-list">
        <div
          v-for="(item, index) in medicineList"
          :key="item.name + '-' + index"
          class="plan-item"
          :class="{ 'plan-item--no-thumb': !item.img }"
        >
          <div class="item-thumb" v-if="item.img">
            <img :src="item.img" :alt="item.name" />
          </div>
          <div class="item-body">
            <div class="item-title" v-if="item.name">{{ item.name }}</div>
            <div v-if="item.effect" class="line">
              {{ item.effect }}
            </div>
            <div v-if="item.disease" class="line">
              {{ item.disease }}
            </div>
            <div v-if="item.composition" class="line">
              {{ item.composition }}
            </div>
            <div v-if="item.cookingMethod" class="line line--pre-line">
              {{ item.cookingMethod }}
            </div>
            <div v-if="item.source" class="line">
              {{ item.source }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-if="hasFoodSection">
      <ReportRibbon title="（三）膳食康养" />
      <div class="food-advice" v-if="food.hobbyFood || food.avoidFood">
        <p v-if="food.hobbyFood">
          <strong>【宜食】</strong>{{ stripFieldLabel(food.hobbyFood, '宜食') }}
        </p>
        <p v-if="food.avoidFood">
          <strong>【忌食】</strong>{{ stripFieldLabel(food.avoidFood, '忌食') }}
        </p>
      </div>
      <div v-if="foodList.length" class="item-list">
        <div
          v-for="(item, index) in foodList"
          :key="item.name + '-' + index"
          class="plan-item"
          :class="{ 'plan-item--no-thumb': !item.img }"
        >
          <div class="item-thumb item-thumb-food" v-if="item.img">
            <img :src="item.img" :alt="item.name" />
          </div>
          <div class="item-body">
            <div class="item-title" v-if="item.name">{{ item.name }}</div>
            <div v-if="item.ingredient" class="line">
              <strong>【药材】</strong>{{ stripFieldLabel(item.ingredient, '药材') }}
            </div>
            <div v-if="item.composition" class="line">
              <strong>【食材】</strong>{{ stripFieldLabel(item.composition, '食材') }}
            </div>
            <div v-if="item.cookingMethod" class="line">
              <strong>【烹饪方法】</strong>{{ stripFieldLabel(item.cookingMethod, '烹饪方法') }}
            </div>
            <div v-if="item.effect" class="line">
              <strong>【功效】</strong>{{ stripFieldLabel(item.effect, '功效') }}
            </div>
            <div v-if="item.source" class="line">
              <strong>【引自】</strong>{{ stripFieldLabel(item.source, '引自') }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="hasHyoxySection" class="plan-section hyoxy-section">
      <ReportRibbon :title="'（四）' + (hyoxy.title || '氢氧康养')" />
      <div class="hyoxy-panel">
        <div class="hyoxy-media" v-if="hyoxy.img">
          <img
            class="hyoxy-image"
            :src="hyoxy.img"
            :alt="hyoxy.title || '氢氧康养'"
          />
          <button
            v-if="hyoxy.goodsUrl"
            type="button"
            class="hyoxy-buy-btn"
            data-report-action="hyoxy-buy"
            @click="$emit('content-action', 'hyoxy-buy')"
          >购买</button>
        </div>
        <div class="hyoxy-advice">
          <p v-for="item in hyoxyAdviceList" :key="item.label" class="hyoxy-device-advice">
            <strong v-if="item.label">{{ item.label }}</strong>{{ item.value }}
          </p>
        </div>
      </div>
    </div>

    <template v-if="hasSportSection">
      <ReportRibbon title="（五）运动保健" />
      <div v-if="sportParagraphs.length" class="paragraphs" data-report-text-block>
        <p v-for="(item, index) in sportParagraphs" :key="'sport-' + index">{{ item }}</p>
      </div>
      <div v-if="eightDeviceList.length" class="item-list device-list">
        <div
          v-if="sportDevice.img || sportDevice.title"
          class="device-group-title"
        >
          <img
            v-if="sportDevice.img"
            class="device-group-icon"
            :src="sportDevice.img"
            :alt="sportDevice.title || ''"
          />
          <span v-if="sportDevice.title">{{ sportDevice.title }}</span>
        </div>
        <div
          v-for="(item, index) in eightDeviceList"
          :key="item.name + '-' + index"
          class="plan-item"
          :class="{ 'plan-item--no-thumb': !item.img }"
        >
          <div class="item-thumb" v-if="item.img">
            <img :src="item.img" :alt="item.name" />
          </div>
          <div class="item-body">
            <div class="item-title" v-if="item.name">{{ item.name }}</div>
            <div v-if="item.method" class="line">
              {{ stripFieldLabel(item.method, '练习方法') }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-if="hasEmotionSection">
      <ReportRibbon title="（六）情志起居" />
      <div v-if="emotionParagraphs.length" class="paragraphs" data-report-text-block>
        <p v-for="(item, index) in emotionParagraphs" :key="'emotion-' + index">{{ item }}</p>
      </div>
      <div v-if="yogaDeviceList.length" class="item-list device-list">
        <div
          v-if="emotionDevice.img || emotionDevice.title"
          class="device-group-title"
        >
          <img
            v-if="emotionDevice.img"
            class="device-group-icon"
            :src="emotionDevice.img"
            :alt="emotionDevice.title || ''"
          />
          <span v-if="emotionDevice.title">{{ emotionDevice.title }}</span>
        </div>
        <div
          v-for="(item, index) in yogaDeviceList"
          :key="item.name + '-' + index"
          class="plan-item"
          :class="{ 'plan-item--no-thumb': !item.img }"
        >
          <div class="item-thumb" v-if="item.img">
            <img :src="item.img" :alt="item.name" />
          </div>
          <div class="item-body">
            <div class="item-title" v-if="item.name">{{ item.name }}</div>
            <div v-if="item.method" class="line">
              {{ stripFieldLabel(item.method, '练习方法') }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="hasSolarTermSection" class="plan-section">
      <ReportRibbon :title="'（七）' + (solarTerm.title || '节气保养')" />
      <div class="solar-term-panel">
        <div v-if="hasSolarTermChart" class="solar-term-chart" aria-hidden="true">
          <img class="solar-term-dial" :src="solarTermDialImage" alt="" />
          <img
            class="solar-term-pointer"
            :src="solarTermPointerImage"
            alt=""
            :style="solarTermPointerStyle"
          />
        </div>
        <div class="solar-term-copy">
          <div class="solar-term-summary">
            <div v-if="solarTerm.currentSolarTerm" class="solar-term-summary-item">
              <span>当前节气：</span>
              <strong>{{ solarTerm.currentSolarTerm }}</strong>
            </div>
            <div v-if="solarTerm.illnessRisk" class="solar-term-summary-item">
              <span>患病风险：</span>
              <strong>{{ solarTerm.illnessRisk }}</strong>
            </div>
          </div>
          <div v-if="solarTerm.riskPrompt" class="solar-term-prompt">
            <strong v-if="solarTerm.name">{{ solarTerm.name }}</strong>{{ solarTerm.riskPrompt }}
          </div>
        </div>
      </div>
    </div>

    <div class="statement" v-if="statementText">
      {{ statementText }}
    </div>
  </section>
</template>

<script>
import ReportRibbon from './ReportRibbon';
import ReportChapterTitle from './ReportChapterTitle';
import {
  hasValueDeep,
  isValidValue,
  splitParagraphs,
  stripFieldLabel,
  toArray,
} from '../helpers';
import { REPORT_ASSETS } from '../reportAssets';

export default {
  name: 'RegulatePlan',
  components: {
    ReportRibbon,
    ReportChapterTitle,
  },
  props: {
    report: {
      type: Object,
      default: () => ({}),
    },
    chapterTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      solarTermDialImage: REPORT_ASSETS.solarTermDial,
      solarTermPointerImage: REPORT_ASSETS.solarTermPointer,
    };
  },
  computed: {
    regulateReport() {
      return this.report.regulateReport || {};
    },
    medicineList() {
      return toArray(this.regulateReport.medicineFood && this.regulateReport.medicineFood.list)
        .filter((item) => hasValueDeep(item));
    },
    food() {
      return this.regulateReport.food || {};
    },
    foodList() {
      return toArray(this.food.list).filter((item) => hasValueDeep(item));
    },
    acupoint() {
      return this.regulateReport.acupoint || {};
    },
    acupointIntro() {
      return this.acupoint.guideText || '';
    },
    acupointList() {
      return toArray(this.acupoint.list).filter((item) => hasValueDeep(item));
    },
    sportDevice() {
      const { sportDevice } = this.regulateReport;
      if (sportDevice && typeof sportDevice === 'object') return sportDevice;
      return { desc: sportDevice || '', eightDevice: [] };
    },
    sportParagraphs() {
      return splitParagraphs(this.sportDevice.desc);
    },
    eightDeviceList() {
      return toArray(this.sportDevice.eightDevice).filter((item) => hasValueDeep(item));
    },
    emotionDevice() {
      const { emotionDevice } = this.regulateReport;
      if (emotionDevice && typeof emotionDevice === 'object') return emotionDevice;
      return { desc: emotionDevice || '', yogaDevice: [] };
    },
    emotionParagraphs() {
      return splitParagraphs(this.emotionDevice.desc);
    },
    yogaDeviceList() {
      return toArray(this.emotionDevice.yogaDevice).filter((item) => hasValueDeep(item));
    },
    solarTerm() {
      const { solarTerm } = this.regulateReport;
      return solarTerm && typeof solarTerm === 'object' ? solarTerm : {};
    },
    hasSolarTermChart() {
      return isValidValue(this.solarTerm.degrees)
        && this.solarTermDialImage
        && this.solarTermPointerImage;
    },
    solarTermPointerStyle() {
      const degrees = Number(this.solarTerm.degrees);
      return {
        transform: ['translate(-50%, -50%) rotate(', Number.isFinite(degrees) ? degrees : 0, 'deg)'].join(''),
      };
    },
    hyoxy() {
      const { hyoxy } = this.regulateReport;
      return hyoxy && typeof hyoxy === 'object' ? hyoxy : {};
    },
    hyoxyAdviceList() {
      const advice = this.hyoxy.tips1;
      if (!advice || typeof advice !== 'object' || !isValidValue(advice.deviceAdvice)) return [];

      return [
        {
          label: isValidValue(this.hyoxy.name) ? String(this.hyoxy.name).trim() : '',
          value: advice.deviceAdvice,
        },
      ];
    },
    statementText() {
      if (!isValidValue(this.report.bottomTips)) return '';
      return String(this.report.bottomTips).trim();
    },
    hasAcupointSection() {
      return Boolean(
        this.acupointIntro
          || this.acupoint.method
          || this.acupoint.getAcupoint
          || this.acupointList.length,
      );
    },
    hasFoodSection() {
      return Boolean(
        this.food.hobbyFood
          || this.food.avoidFood
          || this.foodList.length,
      );
    },
    hasSportSection() {
      return Boolean(this.sportParagraphs.length || this.eightDeviceList.length);
    },
    hasEmotionSection() {
      return Boolean(this.emotionParagraphs.length || this.yogaDeviceList.length);
    },
    hasSolarTermSection() {
      return hasValueDeep(this.solarTerm);
    },
    hasHyoxySection() {
      return Boolean(
        this.hyoxy.img
        || this.hyoxyAdviceList.length,
      );
    },
  },
  methods: {
    stripFieldLabel,
  },
};
</script>

<style lang="scss" scoped>
.blue-note {
  margin-bottom: 10px;
  padding: 9px 12px;
  background: rgba(224, 190, 132, 0.22);
  color: #68311d;
  font-size: 12.5px;
  line-height: 21px;
  font-weight: 700;
  text-align: justify;
}

.item-list {
  margin-bottom: 18px;
}

.acupoint-intro-details,
.acupoint-list {
  break-before: auto;
  page-break-before: auto;
}

.acupoint-intro-details {
  break-inside: auto;
  page-break-inside: auto;
}

.plan-item {
  display: grid;
  grid-template-columns: 54px 1fr;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dotted #9e6c41;
}

.plan-item--no-thumb {
  grid-template-columns: 1fr;
}

.item-thumb {
  width: 54px;
  height: 54px;
  overflow: hidden;
  background: #eef7fb;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

}

.item-title {
  margin-bottom: 5px;
  color: #68311d;
  font-size: 13px;
  line-height: 19px;
  font-weight: 700;
}

.device-group-title {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 0 4px;
  color: #68311d;
  font-size: 13px;
  line-height: 19px;
  font-weight: 700;
}

.device-group-icon {
  display: block;
  width: auto;
  height: 12px;
  flex: none;
  object-fit: contain;
}

.device-list {
  margin-top: -6px;
}

.plan-section {
  break-inside: avoid;
  page-break-inside: avoid;
}

.solar-term-panel {
  display: grid;
  grid-template-columns: 102px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding: 10px 12px;
  border-bottom: 1px dotted #9e6c41;
  background: rgba(224, 190, 132, 0.22);
  break-inside: avoid;
  page-break-inside: avoid;
}

.solar-term-summary {
  display: grid;
  gap: 2px;
  margin-bottom: 4px;
}

.solar-term-summary-item {
  display: flex;
  align-items: baseline;
  color: #68311d;
  font-size: 11.5px;
  line-height: 20px;

  span,
  strong {
    display: inline;
    font-size: inherit;
  }

  strong {
    font-weight: 700;
  }
}

.solar-term-prompt {
  color: #68311d;
  font-size: 11.5px;
  line-height: 20px;
  text-align: justify;
}

.solar-term-chart {
  position: relative;
  width: 102px;
  height: 102px;
}

.solar-term-dial,
.solar-term-pointer {
  position: absolute;
  display: block;
}

.solar-term-dial {
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.solar-term-pointer {
  top: 50%;
  left: 50%;
  width: 88%;
  height: 88%;
  object-fit: contain;
  transform-origin: center;
}

.hyoxy-panel {
  margin-bottom: 18px;
  padding: 10px 0 12px;
  border-bottom: 1px dotted #9e6c41;

  &::after {
    display: block;
    clear: both;
    content: '';
  }
}

.hyoxy-section {
  break-inside: auto;
  page-break-inside: auto;
}

.hyoxy-media {
  position: relative;
  z-index: 10;
  float: left;
  width: 92px;
  margin: 0 12px 8px 0;
  break-inside: avoid;
  page-break-inside: avoid;
}

.hyoxy-image {
  display: block;
  width: 100%;
  max-height: 140px;
  object-fit: contain;
}

.hyoxy-buy-btn {
  position: relative;
  z-index: 20;
  display: block;
  width: 48px;
  height: 22px;
  margin: 6px auto 0;
  padding: 0;
  background: linear-gradient(270deg, #FF3333 0%, #FF6161 100%);
  border: 1px solid #FFFFFF;
  border-radius: 11px;
  color: #FFFFFF;
  font-size: 10px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-appearance: none;
  appearance: none;
  outline: none;
}

.hyoxy-advice {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.hyoxy-advice p {
  margin: 0;
  color: #68311d;
  font-size: 11.5px;
  line-height: 20px;
  text-align: justify;

  & + p {
    margin-top: 4px;
  }

  strong {
    font-weight: 700;
  }
}

.line,
.paragraphs p,
.food-advice p {
  margin: 0;
  color: #68311d;
  font-size: 11.5px;
  line-height: 21px;
  text-align: justify;

  strong {
    color: #68311d;
    font-weight: 700;
  }
}

.hyoxy-device-advice {
  white-space: pre-line;
}

.line--pre-line {
  white-space: pre-line;
}

.paragraphs,
.food-advice {
  margin: 0 0 18px;
  padding-bottom: 10px;
  border-bottom: 1px dotted #9e6c41;
}

.statement {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #9e6c41;
  color: #68311d;
  font-size: 9px;
  line-height: 16px;
  text-align: justify;
}

@media print {
  .hyoxy-buy-btn {
    display: none;
  }
}
</style>
